/**
 * Sporting rules page (/reglement), FR + EN: the full rules V7 as in the design canvas
 * "Gravel Games — Site 2027" (Reglement.dc.html). The French version prevails.
 */
export interface SiteRulesContent {
  docTitle: string;
  back: string;
  title: string;
  sub: string;
  download: string;
  pdf: string;
  pdfFile: string;
  /** Switches the page to the other language. */
  other: string;
  otherCode: 'fr' | 'en';
  draft: string;
  toc: string;
  tocLabel: string;
  articles: { title: string; blocks: { kind: 'text' | 'sub' | 'warn'; text: string }[] }[];
}

export const siteRules: { fr: SiteRulesContent; en: SiteRulesContent } = {
  fr: {
    back: 'Retour aux conditions de participation',
    title: 'Règlement sportif 2027',
    sub: '3–4 avril 2027 · Camp général de Castelnau · La Cavalerie',
    download: 'Télécharger le PDF',
    pdf: '/site/reglement-sportif-fr.pdf',
    pdfFile: 'Reglement_Gravel_Games_2027_FR_V7.pdf',
    other: 'English version',
    otherCode: 'en',
    draft:
      "Règlement sportif V7 de l'édition 2027. Il s'applique à compter de sa publication officielle par l'organisation. En cas de divergence d'interprétation entre les versions linguistiques, la version française prévaut.",
    toc: 'Sommaire',
    tocLabel: 'Sommaire du règlement',
    docTitle: 'Règlement sportif · Gravel Games',
    articles: [
      {
        title: 'Organisation et portée',
        blocks: [
          {
            kind: 'text',
            text: "Les Gravel Games 2027 sont organisés les 3 et 4 avril 2027 par l'association Wish One Racing, au Camp général de Castelnau, La Cavalerie (Aveyron), sous réserve des autorisations administratives et militaires. Toute inscription implique l'acceptation du présent règlement et des consignes de sécurité. L'organisateur peut adapter les parcours, horaires et modalités pour motif légitime de sécurité ou de contraintes militaires, et en informera les participants dès que possible.",
          },
        ],
      },
      {
        title: 'Programme et formats',
        blocks: [
          {
            kind: 'text',
            text: "Gravel Running : 5K, 10K et 20K, courses individuelles chronométrées. Gravel Cycling : 35K randonnée non chronométrée et sans classement ; 70K et 100K courses individuelles chronométrées. Run & Ride : 12K et 24K, compétitions par binômes avec un vélo partagé et alternance libre entre course à pied et vélo, les deux équipiers devant franchir l'arrivée ensemble. Endurance 12H : équipes de 2 à 6, avec en permanence un membre de l'équipe engagé sur la boucle running et un autre sur la boucle cycling, selon le protocole de relais. Les distances sont indicatives. Répartition définitive par jour, départs, boucles et limites horaires : publiés dans le guide participant.",
          },
        ],
      },
      {
        title: 'Âge et inscription',
        blocks: [
          {
            kind: 'text',
            text: "Gravel Running 5K et 10K : à partir de 16 ans révolus, sous réserve des catégories d'âge applicables ; Gravel Running 20K : réservé aux personnes majeures (18 ans révolus). Gravel Cycling 35K (randonnée non chronométrée) : à partir de 16 ans révolus ; Gravel Cycling 70K et 100K : réservés aux majeurs. Run & Ride 12K : à partir de 16 ans révolus pour chacun des deux équipiers ; Run & Ride 24K : réservé aux majeurs (18 ans révolus) pour chacun des deux équipiers ; Endurance 12H : réservée aux majeurs. Tout participant mineur autorisé doit présenter une autorisation parentale écrite et satisfaire aux formalités de santé applicables à son épreuve. Chaque participant est identifié nominativement. Toute substitution exige l'accord de l'organisateur et la validation des justificatifs.",
          },
        ],
      },
      {
        title: 'Santé, licences et justificatifs',
        blocks: [
          {
            kind: 'text',
            text: "GRAVEL RUNNING : licence FFA admissible ou Pass Prévention Santé (PPS) valide pour les majeurs, selon les règles FFA applicables ; pour les mineurs autorisés, questionnaire de santé et attestation avec représentant légal, certificat médical si nécessaire. GRAVEL CYCLING 70K/100K : licence de compétition admise ou justificatif médical/de santé exigé par le cadre cycliste retenu ; le PPS FFA ne se substitue pas automatiquement aux justificatifs cyclistes. GRAVEL CYCLING 35K : randonnée sans classement ni chronométrage ; pour les mineurs de 16 ou 17 ans, autorisation parentale écrite, ainsi que tout justificatif exigé par le cadre retenu. RUN & RIDE 12K/24K : pour chaque équipier, application du cadre FFTRI : licence FFTRI compétition valable pour l'épreuve ou, pour les non-licenciés FFTRI, Licence Expérience FFTRI et formalités de santé correspondantes ; pour les équipiers mineurs de 16 ou 17 ans, autorisation parentale écrite et questionnaire de santé ou certificat médical si requis. Une licence d'une autre fédération ne vaut pas automatiquement licence FFTRI. ENDURANCE 12H : exigences de santé et de licence spécifiques au format mixte à valider avec les fédérations et l'assureur. Aucun départ sans justificatif requis validé.",
          },
        ],
      },
      {
        title: 'Accès au camp et contrôles',
        blocks: [
          {
            kind: 'text',
            text: "Le site est une emprise militaire à accès réglementé. Chaque personne doit suivre les procédures d'identification, d'accès, de circulation et de contrôle communiquées en amont ; une pièce d'identité peut être exigée. Il est interdit de pénétrer dans les zones fermées, de photographier les installations ou personnels signalés comme sensibles, de faire voler un drone sans autorisation expresse et de quitter les parcours ou espaces autorisés. Les consignes des autorités militaires prévalent sur les dispositions sportives en cas de nécessité.",
          },
        ],
      },
      {
        title: 'Parcours et conduite',
        blocks: [
          {
            kind: 'text',
            text: 'Respect impératif du balisage, des points de contrôle, des signaleurs, des autres participants et des éventuels usagers autorisés. Aucun raccourci, aucune assistance motorisée non autorisée. Toute sortie de parcours, infraction de sécurité ou comportement dangereux peut entraîner une pénalité ou une exclusion. Les éventuelles portions ouvertes à la circulation restent soumises au Code de la route.',
          },
        ],
      },
      {
        title: 'Gravel Running : matériel',
        blocks: [
          {
            kind: 'text',
            text: "Dossard officiel visible à l'avant et chaussures adaptées obligatoires. Eau, téléphone chargé, alimentation et vêtement de protection recommandés ; certains éléments peuvent devenir obligatoires selon la météo et la distance. Assistance uniquement dans les zones désignées.",
          },
        ],
      },
      {
        title: 'Gravel Cycling : matériel',
        blocks: [
          {
            kind: 'text',
            text: "Casque homologué correctement attaché, vélo en bon état et identification de course obligatoires. Vélo sans assistance électrique pour les courses chronométrées ; configuration de vélo et équipements autorisés précisés dans le guide participant. Téléphone chargé, matériel de réparation, réserve d'eau, alimentation et protection météo fortement recommandés. L'organisation peut imposer des équipements supplémentaires et refuser un matériel dangereux.",
          },
        ],
      },
      {
        title: 'Run & Ride 12K et 24K',
        blocks: [
          {
            kind: 'text',
            text: "Chaque binôme est constitué de deux participants respectant chacun l'âge minimum de son épreuve : 16 ans pour le 12K et 18 ans pour le 24K et dispose d'un seul vélo sans assistance électrique. Les équipiers alternent librement course à pied et vélo, échangent le vélo de la main à la main sans gain de terrain et doivent rester ensemble selon les écarts autorisés par le règlement technique retenu. Ils franchissent la ligne d'arrivée ensemble. Le casque homologué, attaché, est obligatoire selon les consignes de l'épreuve, y compris lors des alternances. Les conditions définitives de matériel et d'arbitrage seront communiquées avant le départ.",
          },
        ],
      },
      {
        title: 'Endurance 12H : principe et relais',
        blocks: [
          {
            kind: 'text',
            text: "Épreuve réservée aux majeurs, par équipes de 2 à 6 inscrits nominativement. Durant les 12 heures, au moins un équipier doit être engagé sur la boucle Gravel Running et un autre sur la boucle Gravel Cycling, sous réserve des procédures de relais et de neutralisation. Les modalités détaillées de relais, de comptage des tours, de classements, de pénalités et de gestion des interruptions feront l'objet d'un règlement particulier envoyé à chaque équipe avant l'événement. Ce règlement particulier fait partie intégrante du règlement sportif et doit être accepté par chaque équipe avant le départ. Un repas est inclus pour chaque équipier inscrit.",
          },
        ],
      },
      {
        title: 'Chronométrage et classements',
        blocks: [
          {
            kind: 'text',
            text: "Les courses individuelles chronométrées donnent lieu à des classements scratch femmes et hommes ; catégories complémentaires selon le nombre d'inscrits. Les Run & Ride sont classés par binôme. Le classement 12H devra être défini dans son annexe technique : proposition, nombre de tours validés par discipline, puis critères de départage annoncés avant la course. Aucun classement ni chronométrage pour la randonnée 35K. Un classement combiné facultatif pourra être annoncé séparément avec son barème avant l'ouverture des inscriptions.",
          },
        ],
      },
      {
        title: 'Départs, barrières horaires et abandons',
        blocks: [
          {
            kind: 'text',
            text: "Les distances, parcours, horaires de départ, barrières horaires et modalités d'accès au Camp général de Castelnau sont communiqués à titre prévisionnel et peuvent évoluer. Pour des motifs de sécurité, de météo, de contraintes administratives ou de nécessités opérationnelles militaires, l'organisateur peut adapter ou raccourcir les parcours, modifier les horaires et barrières, neutraliser, reporter, interrompre ou annuler tout ou partie des épreuves. Les modifications seront communiquées par les canaux officiels dès que raisonnablement possible. Chaque participant respecte les consignes de contrôle et d'accès au camp. Les conséquences financières éventuelles relèvent des conditions d'annulation et de remboursement publiées lors de l'inscription.",
          },
        ],
      },
      {
        title: 'Sécurité et assistance',
        blocks: [
          {
            kind: 'text',
            text: "Les participants doivent porter assistance à une personne en danger et alerter immédiatement les secours. Le médecin, les secouristes et la direction de course peuvent arrêter la participation d'une personne pour raisons de santé ou de sécurité. Les secours, évacuations, points d'eau et numéros d'urgence figurent dans le guide participant. En cas de météo dangereuse, ordre militaire ou force majeure, l'organisation peut retarder, neutraliser, modifier, interrompre ou annuler une épreuve.",
          },
        ],
      },
      {
        title: 'Esprit Gravel Games et environnement',
        blocks: [
          {
            kind: 'text',
            text: 'Respect du terrain, des installations militaires, des bénévoles, des autres participants et de la faune et flore. Aucun déchet hors zones de collecte. Interdiction de dégrader clôtures, équipements ou espaces naturels. Fair-play et entraide obligatoires.',
          },
        ],
      },
      {
        title: 'Pénalités et réclamations',
        blocks: [
          {
            kind: 'text',
            text: "Peuvent entraîner avertissement, pénalité, déclassement ou disqualification : raccourci, absence à un contrôle, relais irrégulier, fraude, non-port du casque, matériel interdit, assistance illicite, abandon de déchets, comportement dangereux ou refus d'obtempérer. Les décisions sportives sont prises par la direction de course sous réserve des compétences éventuelles des arbitres fédéraux. Réclamation écrite dans les 30 minutes suivant la publication des résultats provisoires, sauf règle fédérale impérative contraire.",
          },
        ],
      },
      {
        title: 'Assurance et responsabilité',
        blocks: [
          {
            kind: 'sub',
            text: 'Responsabilité civile',
          },
          {
            kind: 'text',
            text: 'Conformément à la législation en vigueur, l’association Wish One Racing, organisatrice des Gravel Games, mettra en place une assurance couvrant les conséquences pécuniaires de sa responsabilité civile, de celle de ses préposés et bénévoles ainsi que, dans les limites prévues au contrat, la responsabilité civile pouvant incomber aux participants pendant le déroulement de l’événement.',
          },
          {
            kind: 'text',
            text: 'Cette garantie interviendra en complément ou, à défaut, des assurances personnelles dont les participants peuvent bénéficier par ailleurs. Une attestation d’assurance pourra être communiquée sur simple demande.',
          },
          {
            kind: 'sub',
            text: 'Assurance individuelle accident',
          },
          {
            kind: 'text',
            text: 'La souscription d’une assurance individuelle accident est vivement recommandée. Chaque participant est invité à vérifier que les garanties dont il bénéficie sont adaptées à la pratique sportive, y compris en compétition lorsque cela s’applique.',
          },
          {
            kind: 'text',
            text: 'Les participants ne disposant pas d’une couverture suffisante sont invités à souscrire une assurance complémentaire couvrant notamment les dommages corporels, les frais médicaux, les frais de recherche et de secours, l’assistance, le rapatriement et les dommages au matériel. L’organisation pourra proposer, lors de l’inscription, une solution d’assurance partenaire.',
          },
          {
            kind: 'sub',
            text: 'Dommages matériels',
          },
          {
            kind: 'text',
            text: 'Chaque participant reste responsable de la surveillance de son vélo, de ses équipements et de ses effets personnels. Les limitations éventuelles de responsabilité de l’organisateur s’appliquent uniquement dans les limites autorisées par la loi.',
          },
        ],
      },
      {
        title: 'Annulation, remboursement et modifications',
        blocks: [
          {
            kind: 'sub',
            text: 'Annulation d’inscription par le participant',
          },
          {
            kind: 'text',
            text: 'Une assurance annulation facultative est proposée au moment de l’inscription aux Gravel Games par l’intermédiaire de la plateforme d’inscription et de son partenaire assureur. Elle doit être souscrite avant la validation définitive de la commande. Elle permet, dans les cas et limites prévus par le contrat d’assurance, de demander le remboursement des droits d’inscription versés.',
          },
          {
            kind: 'text',
            text: 'En l’absence de souscription à cette assurance, le désistement volontaire du participant ne donne en principe lieu à aucun remboursement des droits d’inscription par l’organisation, sous réserve des dispositions légales impératives applicables et des conditions générales d’inscription communiquées avant le paiement.',
          },
          {
            kind: 'text',
            text: 'Pour les inscriptions en binôme Run & Ride ou en équipe Endurance 12H, les modalités de prise en charge d’un désistement individuel, de remplacement d’un membre ou de remboursement dépendent des conditions d’inscription et du contrat d’assurance annulation. Aucune couverture automatique de l’ensemble du binôme ou de l’équipe n’est présumée.',
          },
          {
            kind: 'text',
            text: 'L’annulation, le report ou la modification substantielle d’une épreuve par l’organisation relève de dispositions distinctes, prévues par les conditions générales d’inscription et le droit applicable. Le présent article ne limite pas les droits légaux des participants.',
          },
        ],
      },
      {
        title: 'Images et données personnelles',
        blocks: [
          {
            kind: 'text',
            text: "Les prises de vue et leur exploitation sont encadrées par les règles applicables au droit à l'image, à la protection des mineurs et aux données personnelles. Une information distincte précise les finalités, bases légales, destinataires, durées de conservation et droits des participants ; une autorisation spécifique est recueillie lorsque nécessaire, notamment pour les usages promotionnels individualisés. Les données de santé sont limitées aux justificatifs nécessaires et traitées avec des garanties appropriées. Contact RGPD : contact@wishonecycles.com.",
          },
        ],
      },
      {
        title: 'Entrée en vigueur et versions',
        blocks: [
          {
            kind: 'text',
            text: 'Le présent règlement s’applique à l’édition 2027 des Gravel Games à compter de sa publication officielle par l’organisation. En cas de divergence d’interprétation entre les versions linguistiques, la version française prévaut.',
          },
        ],
      },
    ],
  },
  en: {
    back: 'Back to entry requirements',
    title: 'Sporting Rules 2027',
    sub: '3–4 April 2027 · Camp général de Castelnau · La Cavalerie',
    download: 'Download the PDF',
    pdf: '/site/sporting-rules-en.pdf',
    pdfFile: 'Gravel_Games_2027_Sporting_Rules_EN_V7.pdf',
    other: 'Version française',
    otherCode: 'fr',
    draft:
      'Sporting Rules V7 for the 2027 edition. They apply from the date of their official publication by the organiser. In the event of any difference in interpretation between language versions, the French version prevails.',
    toc: 'Contents',
    tocLabel: 'Contents of the rules',
    docTitle: 'Sporting rules · Gravel Games',
    articles: [
      {
        title: 'Organiser and scope',
        blocks: [
          {
            kind: 'text',
            text: 'The 2027 Gravel Games are organised on 3–4 April 2027 by Wish One Racing at Camp général de Castelnau, La Cavalerie, Aveyron, subject to administrative and military authorisations. Entry implies acceptance of these rules and safety instructions. The organiser may adjust routes, schedules or procedures for legitimate safety or military reasons and will inform entrants as soon as reasonably possible.',
          },
        ],
      },
      {
        title: 'Events and formats',
        blocks: [
          {
            kind: 'text',
            text: 'Gravel Running: timed individual 5K, 10K and 20K races. Gravel Cycling: untimed, unranked 35K ride and timed individual 70K and 100K races. Run & Ride: 12K and 24K two-person events using one shared bicycle, with free switching between running and riding and both partners finishing together. 12H Endurance: teams of 2–6 with one runner and one cyclist active at all times on their respective loops. Distances are indicative. Final day-by-day programme, starts, loops and cut-offs will be in the participant guide.',
          },
        ],
      },
      {
        title: 'Age and entry',
        blocks: [
          {
            kind: 'text',
            text: 'Gravel Running 5K and 10K: minimum age 16, subject to applicable federation age categories; Gravel Running 20K: adults only (18 or older). Gravel Cycling 35K (untimed recreational ride): minimum age 16; Gravel Cycling 70K and 100K: adults only. Run & Ride 12K: minimum age 16 for each team member; Run & Ride 24K: adults only (18 or older) for both team members; 12H Endurance: adults only. Every eligible minor must provide written parent/guardian consent and the health documentation applicable to their event. Every participant must be registered by name; substitutions require organiser approval and document checks.',
          },
        ],
      },
      {
        title: 'Health documents and licences',
        blocks: [
          {
            kind: 'text',
            text: 'GRAVEL RUNNING: an accepted FFA licence or valid Pass Prévention Santé (PPS) for adults, under the applicable FFA rules; eligible minors require a health questionnaire and parent/guardian attestation, plus a medical certificate where required. GRAVEL CYCLING 70K/100K: an accepted competition licence or the medical/health documentation required under the chosen cycling framework; an FFA PPS does not automatically replace cycling documents. GRAVEL CYCLING 35K: untimed, unranked recreational ride; riders aged 16 or 17 require written parent/guardian consent and any other documents required under the chosen framework. RUN & RIDE 12K/24K: for each team member, the FFTRI framework applies: a valid FFTRI competition licence accepted for the event or, for those without an FFTRI licence, an FFTRI Licence Expérience and the corresponding health formalities; riders aged 16 or 17 additionally require written parent/guardian consent and a health questionnaire or medical certificate where required. Licences from other federations are not automatically equivalent. 12H ENDURANCE: health and licensing arrangements for the mixed-discipline format require confirmation with the federations and insurer. No start without the required approved documents.',
          },
        ],
      },
      {
        title: 'Military site access',
        blocks: [
          {
            kind: 'text',
            text: 'This is a restricted military site. All visitors must comply with the announced identity, access, movement and security checks; photo ID may be required. Entry to closed areas, photography of designated sensitive facilities or personnel, drone flights without express permission and leaving approved routes are forbidden. Military instructions take priority where required for safety or security.',
          },
        ],
      },
      {
        title: 'Routes and conduct',
        blocks: [
          {
            kind: 'text',
            text: 'Follow official markings, checkpoints, marshals and safety instructions. No shortcuts or unauthorised motorised support. Leaving the course or dangerous behaviour may result in penalties or removal. Where routes use roads open to traffic, road traffic laws apply.',
          },
        ],
      },
      {
        title: 'Gravel Running equipment',
        blocks: [
          {
            kind: 'text',
            text: 'Official bib clearly visible on the front and suitable off-road running shoes are compulsory. Water, charged phone, food and weather protection are recommended; some may be made compulsory depending on conditions. Outside assistance is permitted only in designated areas.',
          },
        ],
      },
      {
        title: 'Gravel Cycling equipment',
        blocks: [
          {
            kind: 'text',
            text: 'A properly fastened certified helmet, roadworthy bicycle and official race identification are compulsory. Electric assistance is not permitted in timed races. Detailed bike configurations and allowed equipment will be specified in the participant guide. Charged phone, repair kit, water, food and weather protection are strongly recommended. Unsafe equipment may be rejected.',
          },
        ],
      },
      {
        title: 'Run & Ride 12K and 24K',
        blocks: [
          {
            kind: 'text',
            text: 'Each pair comprises two participants, each meeting the minimum age for the event: 16 for the 12K and 18 for the 24K and one shared non-electric bicycle. Partners may alternate running and cycling, hand the bicycle directly from one to the other without gaining ground and remain together within the separation limits of the applicable technical rules. Both partners must cross the finish line together. An approved, fastened helmet is mandatory as specified in the event instructions, including during transitions. Final equipment and officiating requirements will be issued before the start.',
          },
        ],
      },
      {
        title: '12H Endurance: participation and relays',
        blocks: [
          {
            kind: 'text',
            text: 'Adults only, in teams of 2–6 named entrants. Throughout the 12 hours, at least one team member must be on the Gravel Running loop and another on the Gravel Cycling loop, subject to the relay and neutralisation procedures. Detailed relay rules, lap counting, rankings, penalties and interruption procedures will be set out in event-specific rules sent to every team before the event. Those rules form an integral part of the sporting regulations and must be accepted by each team before the start. One meal is included for each registered team member.',
          },
        ],
      },
      {
        title: 'Timing and rankings',
        blocks: [
          {
            kind: 'text',
            text: "Timed individual races have women's and men's overall results, with optional further categories. Run & Ride is ranked by pair. The 12H ranking method must be defined in the technical appendix: proposed basis is valid laps in each sport, with tie-break rules published before racing. The 35K ride has no timing or ranking. Any combined challenge will be announced separately with its scoring rules before entries open.",
          },
        ],
      },
      {
        title: 'Starts, cut-offs and withdrawals',
        blocks: [
          {
            kind: 'text',
            text: 'Distances, routes, start times, cut-offs and access arrangements for Camp général de Castelnau are provisional and may change. For safety, weather, administrative or operational military reasons, the organiser may adjust or shorten routes, change start times and cut-offs, neutralise, postpone, stop or cancel all or part of the events. Changes will be communicated through official event channels as soon as reasonably practicable. Entrants must follow all military access and security instructions. Any financial consequences are governed by the cancellation and refund terms published at registration.',
          },
        ],
      },
      {
        title: 'Safety and medical assistance',
        blocks: [
          {
            kind: 'text',
            text: 'Participants must help anyone in danger and alert event emergency staff immediately. The event doctor, first aid personnel or race director may stop a participant for health or safety reasons. Aid posts, water stations and emergency contacts will appear in the participant guide. Dangerous weather, military orders or force majeure may require a delay, neutralisation, rerouting, suspension or cancellation.',
          },
        ],
      },
      {
        title: 'Gravel Games spirit and environment',
        blocks: [
          {
            kind: 'text',
            text: 'Respect the terrain, military facilities, volunteers, fellow participants, wildlife and vegetation. Dispose of litter only in designated areas. Do not damage fences, equipment or natural spaces. Fair play and mutual assistance are required.',
          },
        ],
      },
      {
        title: 'Penalties and protests',
        blocks: [
          {
            kind: 'text',
            text: "Warnings, time penalties, relegation or disqualification may apply for shortcuts, missed checkpoints, invalid relays, fraud, helmet violations, prohibited equipment, unauthorised assistance, littering, dangerous behaviour or refusal to obey instructions. Sporting decisions are made by race management subject to any mandatory federation officials' powers. Written protests must be filed within 30 minutes of provisional results unless binding federation rules provide otherwise.",
          },
        ],
      },
      {
        title: 'Insurance and liability',
        blocks: [
          {
            kind: 'sub',
            text: 'Public liability insurance',
          },
          {
            kind: 'text',
            text: 'In accordance with applicable law, Wish One Racing, the organiser of the Gravel Games, will arrange insurance covering its civil liability and that of its staff and volunteers, as well as, within the terms and limits of the policy, any civil liability of participants covered during the event.',
          },
          {
            kind: 'text',
            text: 'Such cover will operate alongside, or where appropriate in the absence of, participants’ own insurance. An insurance certificate may be provided upon request.',
          },
          {
            kind: 'sub',
            text: 'Personal accident insurance',
          },
          {
            kind: 'text',
            text: 'Personal accident insurance is strongly recommended. Participants should check that their cover is appropriate for the sporting activities involved, including competition where applicable.',
          },
          {
            kind: 'text',
            text: 'Participants without sufficient cover are encouraged to arrange additional insurance for bodily injury, medical expenses, search and rescue costs, assistance, repatriation and equipment damage. The organiser may offer partner insurance during registration.',
          },
          {
            kind: 'sub',
            text: 'Personal property',
          },
          {
            kind: 'text',
            text: 'Participants remain responsible for supervising their bicycles, equipment and personal belongings. Any limitation of the organiser’s liability applies only to the extent permitted by law.',
          },
        ],
      },
      {
        title: 'Cancellation, refunds and changes',
        blocks: [
          {
            kind: 'sub',
            text: 'Cancellation of an entry by a participant',
          },
          {
            kind: 'text',
            text: 'Optional cancellation insurance is offered during Gravel Games registration through the registration platform and its insurance partner. It must be purchased before the order is finally confirmed. Subject to the circumstances, exclusions and limits set out in the policy terms, it allows the participant to seek reimbursement of entry fees paid.',
          },
          {
            kind: 'text',
            text: 'Without cancellation insurance, voluntary withdrawal will generally not entitle the participant to a refund from the organiser, subject to mandatory applicable law and the entry terms disclosed before payment.',
          },
          {
            kind: 'text',
            text: 'For Run & Ride pairs and 12-hour Endurance teams, the treatment of one member’s withdrawal, substitution or any refund depends on the entry terms and the cancellation insurance policy. Cover for the entire pair or team is not automatically assumed.',
          },
          {
            kind: 'text',
            text: 'Cancellation, postponement or material alteration of an event by the organiser is governed separately by the entry terms and applicable law. Nothing in this article limits participants’ statutory rights.',
          },
        ],
      },
      {
        title: 'Image rights and personal data',
        blocks: [
          {
            kind: 'text',
            text: 'Photography and video use are subject to applicable image rights, child protection and data protection law. A separate privacy notice explains purposes, legal bases, recipients, retention and participant rights; specific consent is obtained where required, including for individual promotional uses. Health-related documents are limited to what is necessary and handled with appropriate safeguards. Privacy contact: contact@wishonecycles.com.',
          },
        ],
      },
      {
        title: 'Effective date and language',
        blocks: [
          {
            kind: 'text',
            text: 'These rules apply to the 2027 Gravel Games edition from the date of their official publication by the organiser. In the event of any difference in interpretation between language versions, the French version prevails.',
          },
        ],
      },
    ],
  },
};
